import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { CATEGORY_LABELS, formatPrice } from "../lib/api";
import AdminLogin from "./AdminLogin";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Trash2, Pencil } from "lucide-react";

const STATUSES = ["pending", "paid", "fulfilled", "cancelled"];
const CATS = ["honey", "cider", "maple-syrup", "wine", "apparel"];

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/orders").then((r) => { setOrders(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}`, { status });
    toast.success("Order updated");
    load();
  };

  if (loading) return <div className="py-16 text-center text-[#7C897B]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (orders.length === 0) return <p className="py-16 text-center text-[#7C897B]">No orders yet.</p>;

  return (
    <div className="space-y-4" data-testid="orders-list">
      {orders.map((o) => (
        <div key={o.id} className="bg-white border border-[#E6DFD3] rounded-sm p-5" data-testid={`order-${o.order_number}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-serif text-xl text-[#2D372B]">{o.order_number}</p>
              <p className="text-sm text-[#7C897B]">{o.customer?.name} · {o.customer?.email}</p>
              <p className="text-xs text-[#7C897B] mt-1">{new Date(o.created_at).toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="font-serif text-2xl text-[#B85C38]">{formatPrice(o.total)}</p>
              <span className={`inline-block mt-1 text-xs uppercase tracking-wider px-2 py-1 rounded-sm ${o.payment_status === "paid" ? "bg-[#4A5D4E]/15 text-[#4A5D4E]" : "bg-[#D4A373]/20 text-[#9A4A2B]"}`}>
                {o.payment_status}
              </span>
            </div>
          </div>
          <div className="mt-4 border-t border-[#E6DFD3] pt-3 text-sm text-[#4A5D4E] space-y-1">
            {o.items?.map((it, idx) => (
              <div key={idx} className="flex justify-between">
                <span>{it.quantity}× {it.name}{it.variant ? ` (${it.variant})` : ""}</span>
                <span>{formatPrice(it.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-widest text-[#7C897B]">Status:</span>
            {STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`set-status-${s}-${o.order_number}`}
                onClick={() => updateStatus(o.id, s)}
                className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${o.status === s ? "bg-[#2D372B] text-white border-[#2D372B]" : "border-[#E6DFD3] text-[#4A5D4E] hover:border-[#B85C38]"}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const emptyProduct = { name: "", category: "honey", description: "", price: 0, sale_price: "", image: "", featured: false, active: true, variant_label: "", variants: [] };

function ProductForm({ initial, onSaved, onClose }) {
  const [p, setP] = useState(initial || emptyProduct);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const inputCls = "w-full border border-[#E6DFD3] rounded-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#B85C38]/40";

  const addVariant = () => set("variants", [...(p.variants || []), { label: "", price: 0, stock: 50 }]);
  const updVariant = (i, k, v) => set("variants", p.variants.map((vr, idx) => idx === i ? { ...vr, [k]: v } : vr));
  const rmVariant = (i) => set("variants", p.variants.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!p.name) { toast.error("Name is required"); return; }
    setSaving(true);
    const body = {
      ...p,
      price: parseFloat(p.price) || 0,
      sale_price: p.sale_price === "" || p.sale_price === null ? null : parseFloat(p.sale_price),
      variants: (p.variants || []).map((v) => ({ label: v.label, price: parseFloat(v.price) || 0, stock: parseInt(v.stock) || 0 })),
    };
    try {
      if (p.id) await api.put(`/admin/products/${p.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Product saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Could not save product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <input className={inputCls} placeholder="Product name" value={p.name} onChange={(e) => set("name", e.target.value)} data-testid="product-form-name" />
      <select className={inputCls} value={p.category} onChange={(e) => set("category", e.target.value)} data-testid="product-form-category">
        {CATS.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
      </select>
      <textarea className={inputCls} rows={3} placeholder="Description" value={p.description} onChange={(e) => set("description", e.target.value)} />
      <input className={inputCls} placeholder="Image URL" value={p.image} onChange={(e) => set("image", e.target.value)} data-testid="product-form-image" />
      <div className="grid grid-cols-2 gap-3">
        <input className={inputCls} type="number" step="0.01" placeholder="Price" value={p.price} onChange={(e) => set("price", e.target.value)} data-testid="product-form-price" />
        <input className={inputCls} type="number" step="0.01" placeholder="Sale price (optional)" value={p.sale_price ?? ""} onChange={(e) => set("sale_price", e.target.value)} />
      </div>
      <div className="flex items-center gap-6 text-sm">
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={p.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label>
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={p.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
      </div>

      <div className="border-t border-[#E6DFD3] pt-4">
        <div className="flex items-center justify-between">
          <p className="kicker text-[#7C897B]">Variants (optional)</p>
          <button onClick={addVariant} className="text-xs text-[#B85C38] flex items-center gap-1" data-testid="add-variant"><Plus className="w-3 h-3" /> Add</button>
        </div>
        {p.variants?.length > 0 && (
          <input className={`${inputCls} mt-3`} placeholder="Variant label (e.g. Size)" value={p.variant_label || ""} onChange={(e) => set("variant_label", e.target.value)} />
        )}
        <div className="space-y-2 mt-2">
          {p.variants?.map((v, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input className={inputCls} placeholder="Option (e.g. 8 oz)" value={v.label} onChange={(e) => updVariant(i, "label", e.target.value)} />
              <input className={inputCls} type="number" step="0.01" placeholder="Price" value={v.price} onChange={(e) => updVariant(i, "price", e.target.value)} />
              <button onClick={() => rmVariant(i)} className="text-[#B85C38] p-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <DialogFooter>
        <button onClick={save} disabled={saving} className="bg-[#B85C38] hover:bg-[#9A4A2B] text-white uppercase tracking-widest text-sm px-6 py-3 rounded-sm" data-testid="save-product-button">
          {saving ? "Saving…" : "Save Product"}
        </button>
      </DialogFooter>
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/products").then((r) => { setProducts(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setOpen(true); };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-[#2D372B] text-white uppercase tracking-widest text-xs px-5 py-3 rounded-sm" data-testid="new-product-button">
          <Plus className="w-4 h-4" /> New Product
        </button>
      </div>
      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#7C897B]" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-products-list">
          {products.map((p) => (
            <div key={p.id} className="bg-white border border-[#E6DFD3] rounded-sm overflow-hidden" data-testid={`admin-product-${p.slug}`}>
              <img src={p.image} alt={p.name} className="w-full h-40 object-cover" />
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <p className="kicker">{CATEGORY_LABELS[p.category]}</p>
                  <div className="flex gap-1">
                    {p.featured && <span className="text-[10px] bg-[#D4A373]/30 text-[#9A4A2B] px-2 py-0.5 rounded-sm uppercase tracking-wider">Featured</span>}
                    {!p.active && <span className="text-[10px] bg-[#E6DFD3] text-[#7C897B] px-2 py-0.5 rounded-sm uppercase tracking-wider">Hidden</span>}
                  </div>
                </div>
                <p className="font-serif text-lg text-[#2D372B] mt-1">{p.name}</p>
                <p className="text-sm text-[#B85C38]">{p.variants?.length ? `from ${formatPrice(Math.min(...p.variants.map(v => v.price)))}` : formatPrice(p.sale_price || p.price)}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEdit(p)} className="flex-1 inline-flex items-center justify-center gap-1 border border-[#E6DFD3] hover:border-[#B85C38] text-[#4A5D4E] text-xs uppercase tracking-wider py-2 rounded-sm" data-testid={`edit-${p.slug}`}><Pencil className="w-3 h-3" /> Edit</button>
                  <button onClick={() => del(p.id)} className="inline-flex items-center justify-center border border-[#E6DFD3] hover:border-[#B85C38] text-[#B85C38] px-3 py-2 rounded-sm" data-testid={`delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF8F5] max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <ProductForm initial={editing} onSaved={load} onClose={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);

  const save = async () => {
    await api.put("/admin/settings", {
      ...s,
      shipping_flat: parseFloat(s.shipping_flat) || 0,
      free_shipping_threshold: parseFloat(s.free_shipping_threshold) || 0,
    });
    toast.success("Storefront settings saved");
  };

  if (!s) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#7C897B]" /></div>;
  const inputCls = "w-full border border-[#E6DFD3] rounded-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#B85C38]/40";

  return (
    <div className="max-w-xl bg-white border border-[#E6DFD3] rounded-sm p-6 space-y-5" data-testid="settings-form">
      <div>
        <label className="kicker text-[#7C897B]">Announcement Banner</label>
        <input className={`${inputCls} mt-2`} value={s.announcement} onChange={(e) => set("announcement", e.target.value)} data-testid="setting-announcement" />
        <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer"><input type="checkbox" checked={s.announcement_active} onChange={(e) => set("announcement_active", e.target.checked)} /> Show banner</label>
      </div>
      <div>
        <label className="kicker text-[#7C897B]">Hero Tagline</label>
        <input className={`${inputCls} mt-2`} value={s.hero_tagline} onChange={(e) => set("hero_tagline", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="kicker text-[#7C897B]">Flat Shipping ($)</label>
          <input className={`${inputCls} mt-2`} type="number" step="0.01" value={s.shipping_flat} onChange={(e) => set("shipping_flat", e.target.value)} />
        </div>
        <div>
          <label className="kicker text-[#7C897B]">Free Shipping Over ($)</label>
          <input className={`${inputCls} mt-2`} type="number" step="0.01" value={s.free_shipping_threshold} onChange={(e) => set("free_shipping_threshold", e.target.value)} />
        </div>
      </div>
      <button onClick={save} className="bg-[#B85C38] hover:bg-[#9A4A2B] text-white uppercase tracking-widest text-sm px-6 py-3 rounded-sm" data-testid="save-settings-button">Save Settings</button>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, isAdmin, loading, logout } = useAuth();

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><Loader2 className="w-8 h-8 animate-spin text-[#B85C38]" /></div>;
  if (!user || !isAdmin) return <AdminLogin />;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <div className="border-b border-[#E6DFD3] bg-white">
        <div className="px-6 md:px-12 max-w-7xl mx-auto h-20 flex items-center justify-between">
          <div>
            <p className="kicker">Wayside Orchard</p>
            <h1 className="font-serif text-2xl text-[#2D372B]">Store Admin</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#7C897B] hidden sm:block">{user.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-[#4A5D4E] hover:text-[#B85C38]" data-testid="logout-button"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-12 max-w-7xl mx-auto py-10">
        <Tabs defaultValue="orders">
          <TabsList className="bg-[#F0EBE1] mb-8">
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Storefront</TabsTrigger>
          </TabsList>
          <TabsContent value="orders"><OrdersTab /></TabsContent>
          <TabsContent value="products"><ProductsTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
